import { useMemo, useState } from "react";
import { formatScore } from "../../CoTrader/Utilities/updateDisplay";
import { capitalize } from "../DashboardUtils";
import "../ProductDashboard.css";

const MAX_READINGS = 4;
const TONE_EPSILON = 0.05;

const getTone = (value) =>
  value > TONE_EPSILON ? "positive" : value < -TONE_EPSILON ? "negative" : "neutral";

// Last two non-null points of a series: [date, score, isin] tuples.
const getLatestReading = (series) => {
  const points = (series?.data || []).filter(
    (point) => point && Number.isFinite(point[1])
  );
  if (!points.length) return null;

  const latest = points[points.length - 1];
  const previous = points.length > 1 ? points[points.length - 2] : null;
  return {
    value: latest[1],
    date: latest[0],
    delta: previous ? latest[1] - previous[1] : null,
  };
};

/**
 * Latest score per charted series, strongest reading first. Everything shown
 * comes straight from the chart data; series without a reading are skipped.
 */
export const SeriesReadings = ({ series }) => {
  const readings = useMemo(() => {
    const showCountry =
      new Set((series || []).map((entry) => entry.country)).size > 1;

    return (series || [])
      .map((entry) => {
        const reading = getLatestReading(entry);
        if (!reading) return null;
        return {
          key: entry.isin || `${entry.name}-${entry.country}`,
          label: showCountry
            ? `${entry.name} · ${capitalize(entry.country || "")}`
            : entry.name,
          ...reading,
        };
      })
      .filter(Boolean)
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  }, [series]);

  if (!readings.length) return null;

  const visible = readings.slice(0, MAX_READINGS);
  const hiddenCount = readings.length - visible.length;

  return (
    <div className="pd-readings" aria-label="Latest readings">
      {visible.map((reading) => (
        <div
          key={reading.key}
          className={`pd-reading pd-reading--${getTone(reading.value)}`}
          title={`${reading.label}: ${reading.value.toFixed(4)} on ${reading.date.toLocaleDateString("en-GB")}`}
        >
          <div className="pd-reading-name">{reading.label}</div>
          <div className="pd-reading-value">
            {formatScore(reading.value)}
            {reading.delta != null && Math.abs(reading.delta) >= 0.005 && (
              <span className="pd-reading-delta">
                {reading.delta > 0 ? "▲" : "▼"} {Math.abs(reading.delta).toFixed(2)}
              </span>
            )}
          </div>
        </div>
      ))}
      {hiddenCount > 0 && <span className="pd-reading-more">+{hiddenCount} more</span>}
    </div>
  );
};

// "12 series · Daily · 1 Jan 2026 – 28 Sep 2026", from the loaded chart.
export const getChartMeta = (series, granularity) => {
  const count = series?.length || 0;
  if (!count) return "";

  let first = null;
  let last = null;
  series.forEach((entry) => {
    (entry.data || []).forEach((point) => {
      const date = point?.[0];
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;
      if (!first || date < first) first = date;
      if (!last || date > last) last = date;
    });
  });

  const format = (date) =>
    date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const granularityLabel = granularity
    ? capitalize(String(granularity).toLowerCase())
    : "";

  return [
    `${count} series`,
    granularityLabel,
    first && last ? `${format(first)} – ${format(last)}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
};

/** Top positive / negative names for the active category; click to focus. */
export const SentimentMovers = ({ positives = [], negatives = [], onSelect }) => {
  const [view, setView] = useState("positive");
  const items = view === "positive" ? positives : negatives;

  return (
    <div className="pd-movers">
      <div className="pd-seg" role="tablist" aria-label="Top movers">
        {[
          ["positive", "Top positive"],
          ["negative", "Top negative"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={view === key}
            className={`pd-seg-btn ${view === key ? "is-active" : ""}`}
            onClick={() => setView(key)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="pd-movers-list">
        {items.map((item, index) => (
          <button
            key={`${view}-${item.company}-${item.country}-${index}`}
            type="button"
            className={`pd-mover pd-mover--${view}`}
            onClick={() => onSelect?.(item.company, item.country)}
            title={`Focus the chart on ${item.company}`}
          >
            <span className="pd-mover-name">{item.company}</span>
            {item.country && (
              <span className="pd-mover-country">{capitalize(item.country)}</span>
            )}
            {Number.isFinite(Number(item.score)) && (
              <span className="pd-mover-score">{formatScore(Number(item.score))}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};
